import api from '../../config/axios';

// ============================================================================
// STATIC DATA CACHE
// Cache for frequently accessed static data to improve performance
// ============================================================================
let stagesCache = null;
let categoriesCache = null;
let governoratesCache = null;
let publicStatsCache = null;

// ============================================================================
// AUTHENTICATION SERVICE API
// All endpoints for user authentication and profile management
// ============================================================================

export const authService = {
  // ============================================================================
  // AUTHENTICATION ENDPOINTS
  // ============================================================================
  
  /**
   * User login
   * POST /api/auth/login
   * Purpose: Authenticate user and get access token
   * @param {Object} credentials - User login credentials (email, password)
   * @returns {Promise} Response with user data and token
   */
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  /**
   * User registration
   * POST /api/auth/register
   * Purpose: Create new user account
   * @param {Object} userData - User registration data (name, email, password, etc.)
   * @returns {Promise} Response with created user data
   */
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  /**
   * User logout
   * POST /api/auth/logout
   * Purpose: End user session and invalidate token
   * @returns {Promise} Response confirming logout
   */
  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  /**
   * Get user profile
   * GET /api/auth/profile
   * Purpose: Get current authenticated user's profile information
   * @returns {Promise} Response with user profile data
   */
  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  // ============================================================================
  // USER MANAGEMENT ENDPOINTS
  // ============================================================================
  
  /**
   * Change user password
   * PUT /api/auth/password
   * Purpose: Update user's password
   * @param {Object} passwordData - Current and new password
   * @returns {Promise} Response confirming password change
   */
  changePassword: async (passwordData) => {
    const response = await api.put('/auth/password', passwordData);
    return response.data;
  },

  /**
   * Update user profile
   * PUT /api/auth/profile
   * Purpose: Update user's profile information
   * @param {Object} profileData - Updated profile data
   * @returns {Promise} Response with updated profile
   */
  updateProfile: async (profileData) => {
    const response = await api.put('/auth/profile', profileData);
    return response.data;
  },

  // ============================================================================
  // EMAIL VERIFICATION API
  // ============================================================================
  
  /**
   * Request email verification
   * POST /api/auth/request-verification
   * Purpose: Send verification email to user
   * @param {Object} data - Email and user info
   * @returns {Promise} Response with verification request status
   */
  requestVerification: async (data) => {
    const response = await api.post('/auth/request-verification', data);
    return response.data;
  },

  /**
   * Verify email with token
   * GET /api/auth/verify-email/:token
   * Purpose: Verify user email using token
   * @param {string} token - Verification token from email
   * @returns {Promise} Response with verification status
   */
  verifyEmail: async (token) => {
    const response = await api.get(`/auth/verify-email/${token}`);
    return response.data;
  },

  // ============================================================================
  // PASSWORD RESET (FORGOT PASSWORD) API
  // ============================================================================

  /**
   * Request password reset link
   * POST /api/auth/forgot-password
   * Purpose: Send password reset email to user
   * @param {Object} data - { email }
   * @returns {Promise} Response with generic message
   */
  forgotPassword: async (data) => {
    const response = await api.post('/auth/forgot-password', data);
    return response.data;
  },

  /**
   * Reset password using token
   * POST /api/auth/reset-password
   * Purpose: Set new password using a one-time token
   * @param {Object} data - { token, newPassword }
   * @returns {Promise} Response with status
   */
  resetPassword: async (data) => {
    const response = await api.post('/auth/reset-password', data);
    return response.data;
  },

  // ============================================================================
  // SESSION MANAGEMENT API
  // ============================================================================
  
  /**
   * Get registration data
   * GET /api/auth/register-data
   * Purpose: Get subjects and grades for registration
   * @returns {Promise} Response with registration data
   */
  getRegisterData: async () => {
    const response = await api.get('/auth/register-data');
    return response.data;
  },

  /**
   * Logout all sessions
   * POST /api/auth/logout-all
   * Purpose: Logout user from all devices
   * @param {Object} data - User session info
   * @returns {Promise} Response confirming logout
   */
  logoutAll: async (data) => {
    const response = await api.post('/auth/logout-all', data);
    return response.data;
  },

  /**
   * Get active sessions
   * GET /api/auth/sessions
   * Purpose: Get all active user sessions
   * @returns {Promise} Response with sessions data
   */
  getSessions: async () => {
    const response = await api.get('/auth/sessions');
    return response.data;
  },

  /**
   * Get public courses (featured courses for homepage)
   * GET /api/public/courses
   * Purpose: Get featured/public courses for homepage display
   * @param {Object} params - Query parameters (limit, featured, etc.)
   * @returns {Promise} Response with courses array
   */
  getPublicCourses: async (params = {}) => {
    const response = await api.get('/public/courses', { params });
    return response.data;
  },

  /**
   * Get public platform statistics
   * GET /api/public/stats
   * Purpose: Get platform statistics for homepage display
   * @returns {Promise} Response with platform stats
   */
  getPublicStats: async () => {
    if (publicStatsCache) return publicStatsCache;
    const response = await api.get('/public/stats');
    publicStatsCache = response.data;
    return publicStatsCache;
  },

  /**
   * Search teachers publicly
   * GET /api/public/teachers
   * Purpose: Search and filter teachers for public display
   * @param {Object} params - Search parameters (search, subject, grade, etc.)
   * @param {AbortSignal} signal - Abort signal for request cancellation
   * @returns {Promise} Response with teachers array
   */
  searchTeachers: async (params = {}, signal = null) => {
    const config = { params };
    if (signal) {
      config.signal = signal;
    }
    const response = await api.get('/public/teachers', config);
    return response.data;
  },

  /**
   * Search courses publicly
   * GET /api/public/courses
   * Purpose: Search and filter courses for public display
   * @param {Object} params - Search parameters (search, limit, etc.)
   * @param {AbortSignal} signal - Abort signal for request cancellation
   * @returns {Promise} Response with courses array
   */
  searchCourses: async (params = {}, signal = null) => {
    const config = { params };
    if (signal) {
      config.signal = signal;
    }
    const response = await api.get('/public/courses', config);
    return response.data;
  },

  /**
   * Get stages with grades for filtering
   * GET /api/public/stages
   * Purpose: Get educational stages with nested grades for filters
   * @returns {Promise} Response with stages array
   */
  getStages: async () => {
    if (stagesCache) return stagesCache;
    const response = await api.get('/public/stages');
    stagesCache = response.data;
    return stagesCache;
  },

  /**
   * Get categories/subjects
   * GET /api/public/subjects
   * Purpose: Get available subjects/categories for filtering
   * @returns {Promise} Response with categories array
   */
  getCategories: async () => {
    if (categoriesCache) return categoriesCache;
    const response = await api.get('/public/subjects');
    categoriesCache = response.data;
    return categoriesCache;
  },

  /**
   * Get all governorates
   * GET /api/public/governorates
   * Purpose: Get list of all governorates for location selection
   * @returns {Promise} Response with governorates array
   */
  getGovernorates: async () => {
    if (governoratesCache) return governoratesCache;
    const response = await api.get('/public/governorates');
    governoratesCache = response.data;
    return governoratesCache;
  },
};

export default authService;
